import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { translate } from "@/lib/i18n";
import api from "@/lib/api";

const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    if (typeof window === "undefined") return "en";
    return localStorage.getItem("vedari_lang") || "en";
  });
  const [synced, setSynced] = useState(false);

  useEffect(() => {
    localStorage.setItem("vedari_lang", lang);
    document.documentElement.lang = lang;
  }, [lang]);

  // On first load, sync from server (overrides local if user has set on another device)
  useEffect(() => {
    if (synced) return;
    (async () => {
      try {
        const r = await api.get("/auth/me");
        const serverLang = r.data?.language;
        if (serverLang && serverLang !== lang) {
          setLangState(serverLang);
        }
      } catch {}
      setSynced(true);
    })();
  // eslint-disable-next-line
  }, []);

  const setLang = useCallback((next) => {
    setLangState(next);
    // Fire-and-forget — Menu cycleLang also persists explicitly
    api.put("/me/preferences", { language: next }).catch(() => {});
  }, []);

  const t = useCallback((key, params) => translate(lang, key, params), [lang]);

  return (
    <LanguageContext.Provider value={{ lang, setLang, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLang() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLang must be used within LanguageProvider");
  return ctx;
}
